// Authentication logging utility for debugging session issues
export const AuthLogger = {
  // Log levels
  INFO: "INFO",
  WARN: "WARN",
  ERROR: "ERROR",

  // Log authentication events
  log(level, event, data = {}) {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      level,
      event,
      ...data,
      // Add session context
      userId: JSON.parse(localStorage.getItem("user"))?.id,
      sessionAge: this.getSessionAge(),
    };

    // Console log with color coding
    const color =
      level === this.ERROR
        ? "color: red"
        : level === this.WARN
          ? "color: orange"
          : "color: blue";
    console.log(`%c[Auth ${level}] ${event}`, color, logEntry);

    // Store in session storage for debugging (last 50 events)
    const logs = JSON.parse(sessionStorage.getItem("authLogs") || "[]");
    logs.push(logEntry);
    if (logs.length > 50) logs.shift();
    sessionStorage.setItem("authLogs", JSON.stringify(logs));
  },

  // Calculate how long the current session has been active
  getSessionAge() {
    const tokens = JSON.parse(localStorage.getItem("authTokens"));
    if (!tokens?.access_token) return null;

    try {
      // Decode JWT to get issued at time
      const payload = JSON.parse(atob(tokens.access_token.split(".")[1]));
      const issuedAt = payload.iat * 1000; // Convert to milliseconds
      return Math.round((Date.now() - issuedAt) / 1000); // Return age in seconds
    } catch (e) {
      return null;
    }
  },

  // Specific event loggers
  loginAttempt(username) {
    this.log(this.INFO, "LOGIN_ATTEMPT", { username });
  },

  loginSuccess(user) {
    this.log(this.INFO, "LOGIN_SUCCESS", {
      userId: user.id,
      email: user.email,
    });
  },

  loginFailed(error) {
    this.log(this.ERROR, "LOGIN_FAILED", {
      error: error.message,
      status: error.status,
    });
  },

  tokenRefreshStarted(reason = "scheduled") {
    const tokens = JSON.parse(localStorage.getItem("authTokens"));
    const expiresIn = this.getTokenExpiresIn(tokens?.access_token);

    this.log(this.INFO, "TOKEN_REFRESH_STARTED", {
      reason,
      tokenExpiresIn: expiresIn,
    });
  },

  tokenRefreshSuccess() {
    const tokens = JSON.parse(localStorage.getItem("authTokens"));
    const newExpiresIn = this.getTokenExpiresIn(tokens?.access_token);

    this.log(this.INFO, "TOKEN_REFRESH_SUCCESS", {
      newTokenExpiresIn: newExpiresIn,
    });
  },

  tokenRefreshFailed(error, attemptNumber) {
    this.log(this.ERROR, "TOKEN_REFRESH_FAILED", {
      error: error.message,
      status: error.status,
      attemptNumber,
      willRetry: error.status !== 401 && error.status !== 403,
    });
  },

  tokenExpired() {
    this.log(this.WARN, "TOKEN_EXPIRED");
  },

  logout(reason = "manual") {
    this.log(this.INFO, "LOGOUT", { reason });
  },

  networkError(operation, error) {
    this.log(this.ERROR, "NETWORK_ERROR", {
      operation,
      error: error.message,
    });
  },

  // Helper to get token expiration time in seconds
  getTokenExpiresIn(token) {
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const expiresAt = payload.exp * 1000; // Convert to milliseconds
      return Math.round((expiresAt - Date.now()) / 1000); // Return seconds until expiry
    } catch (e) {
      return null;
    }
  },

  // Export logs for debugging
  exportLogs() {
    return JSON.parse(sessionStorage.getItem("authLogs") || "[]");
  },

  // Clear logs
  clearLogs() {
    sessionStorage.removeItem("authLogs");
  },
};
